import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import WelcomeScreen from './components/WelcomeScreen';
import StatisticsScreen from './components/StatisticsScreen';
import RoleSelection from './components/RoleSelection';
import AuthScreen from './components/AuthScreen';
import AuthPage from './components/AuthPage';
import LawyerRegistration from './components/LawyerRegistration';
import Dashboard from './components/Dashboard';
import LawyerDashboard from './components/LawyerDashboard';
import LawyerProfile from './components/LawyerProfile';
import LawyerSettings from './components/LawyerSettings';
import LawyerLayout from './components/LawyerLayout';
import LawyerCases from './components/LawyerCases';
import LawyerWallet from './components/LawyerWallet';
import LawyerAIAssistant from './components/LawyerAIAssistant';
import LawyerChats from './components/LawyerChats';
import LawyerChatConversation from './components/LawyerChatConversation';
import LawyerRescheduleAppointment from './components/LawyerRescheduleAppointment';
import LawyerProfileSettings from './components/LawyerProfileSettings';
import LawyerWorkingHours from './components/LawyerWorkingHours';
import LawyerSetAvailability from './components/LawyerSetAvailability';
import LawyerRatingsReviews from './components/LawyerRatingsReviews';
import LawyerSearch from './components/LawyerSearch';
import BookingCreate from './components/BookingCreate';
import PendingApprovalScreen from './components/PendingApprovalScreen';
import Chat from './components/Chat';
import PaymentProcessor from './components/PaymentProcessor';

import FindLawyer from './components/FindLawyer';
import LawyerDetail from './components/LawyerDetail';
import MyBookings from './components/MyBookings';
import AiAssistant from './components/AiAssistant';
import Settings from './components/Settings';
import ChatDetail from './components/ChatDetail';
import Chats from './components/Chats';
import ChatConversation from './components/ChatConversation';
import AppLayout from './components/AppLayout';
import Plans from './components/Plans';
import Payment from './components/Payment';
import Wallet from './components/Wallet';
import UserProfile from './components/UserProfile';
import DocumentManager from './components/DocumentManager';
import AdvancedAnalytics from './components/AdvancedAnalytics';
import PWARegistration from './components/PWARegistration';
import { FirebaseAuthProvider } from './context/FirebaseAuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RegisterScreen from './components/RegisterScreen';
import ResponsiveTest from './components/ResponsiveTest';
import DiagnosticPage from './components/DiagnosticPage';
import WebOnlyAdminDashboard from './components/WebOnlyAdminDashboard';
import AdminLogin from './components/AdminLogin';
import FirestoreSetup from './components/FirestoreSetup';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import DataCleanup from './components/DataCleanup';
import { AppProvider } from './context/AppContext';
import { useApp } from './context/useApp';
import autoCancelService from './services/autoCancelService';
import { Toaster } from 'react-hot-toast';

// Route wrapper components - moved inside AppRoutes to have access to context

function AppRoutes() {
  // Safe access to useApp with fallback
  let isAuthenticated = false;
  let userRole = null;
  let isProfileComplete = false;
  let hasActiveSubscription = false;
  let contextAvailable = false;
  let isLoading = false;
  
  try {
    const appContext = useApp();
    if (appContext) {
      contextAvailable = true;
      isAuthenticated = appContext.isAuthenticated || false;
      userRole = appContext.userRole;
      isProfileComplete = appContext.isProfileComplete || false;
      hasActiveSubscription = appContext.hasActiveSubscription || false;
      isLoading = appContext.isLoading || false;
    }
  } catch (error) {
    console.log('useApp context not available yet, using defaults:', error.message);
    // Return a loading state instead of crashing
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }
  
  console.log('AppRoutes - Auth state:', {
    isAuthenticated,
    userRole,
    isProfileComplete,
    hasActiveSubscription,
    isLoading,
    contextAvailable,
    currentPath: window.location.pathname
  });

  // If context is not available yet, show loading
  if (!contextAvailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing application...</p>
        </div>
      </div>
    );
  }

  // Role-based route component
  const RoleBasedRoute = ({ allowedRoles, children, fallbackPath = '/role-selection' }) => {
    if (!isAuthenticated) {
      return <Navigate to="/auth" replace />;
    }
    
    // If userRole is not set yet, show loading - let the auth flow complete
    // But also check if we're in a loading state
    if (userRole === undefined || !contextAvailable || isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-primary">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-text-secondary">Loading user data...</p>
          </div>
        </div>
      );
    }
    
    // If no role is set, redirect to role selection
    if (!userRole) {
      return <Navigate to="/role-selection" replace />;
    }
    
    if (!allowedRoles.includes(userRole)) {
      // Redirect based on role and profile completion
      if (userRole === 'lawyer') {
        if (isProfileComplete) {
          return <Navigate to="/lawyer-dashboard" replace />;
        } else {
          return <Navigate to="/lawyer-profile" replace />;
        }
      } else if (userRole === 'admin') {
        return <Navigate to="/admin-dashboard" replace />;
      } else if (userRole === 'client') {
        if (isProfileComplete) {
          return <Navigate to="/dashboard" replace />;
        } else {
          return <Navigate to="/profile" replace />;
        }
      } else {
        return <Navigate to="/role-selection" replace />;
      }
    }
    
    return children;
  };

  // Profile completion check component
  const ProfileRequiredRoute = ({ children, fallbackPath = '/profile' }) => {
    // If profile completion status is not determined yet, allow access (let the component handle it)
    if (isProfileComplete === null || isProfileComplete === undefined) {
      return children;
    }
    
    if (!isProfileComplete) {
      return <Navigate to={fallbackPath} replace />;
    }
    
    return children;
  };

  // Plan required route component
  const PlanRequiredRoute = ({ children, fallbackPath = '/plans' }) => {
    // If subscription status is not determined yet, don't redirect
    if (hasActiveSubscription === null || hasActiveSubscription === undefined) {
      return <Navigate to="/role-selection" replace />;
    }
    
    if (!hasActiveSubscription) {
      return <Navigate to={fallbackPath} replace />;
    }
    
    return children;
  };

  return (
    <Routes>
      {/* Public routes - no authentication required */}
      <Route path="/" element={<WelcomeScreen />} />
      <Route path="/register" element={<RegisterScreen />} />
      <Route path="/role-selection" element={<RoleSelection />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/responsive-test" element={<ResponsiveTest />} />
      <Route path="/diagnostic" element={<DiagnosticPage />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/firestore-setup" element={<FirestoreSetup />} />
      
      {/* Admin routes */}
      <Route path="/admin-dashboard" element={
        <AdminProtectedRoute>
          <WebOnlyAdminDashboard />
        </AdminProtectedRoute>
      } />
      
      <Route path="/data-cleanup" element={
        <AdminProtectedRoute>
          <DataCleanup />
        </AdminProtectedRoute>
      } />
      
      {/* Statistics route - public route for new user flow */}
      <Route path="/intro/statistics" element={<StatisticsScreen />} />

      {/* Client routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout>
            <UserProfile />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/plans" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <Plans />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/find-lawyer" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <PlanRequiredRoute>
                <AppLayout>
                  <FindLawyer />
                </AppLayout>
              </PlanRequiredRoute>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-detail/:id" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <PlanRequiredRoute>
                <AppLayout>
                  <LawyerDetail />
                </AppLayout>
              </PlanRequiredRoute>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/booking/create" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <PlanRequiredRoute>
                <AppLayout>
                  <BookingCreate />
                </AppLayout>
              </PlanRequiredRoute>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/my-bookings" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <MyBookings />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/chats" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <Chats />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/chat-conversation/:chatId" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <ChatConversation />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/chat-detail" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <ChatDetail />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/ai-assistant" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <AiAssistant />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <Settings />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/wallet" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <Wallet />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/documents" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <DocumentManager />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/analytics" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <AdvancedAnalytics />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/payment" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <Payment />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/payment/process" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['client']}>
            <ProfileRequiredRoute>
              <AppLayout>
                <PaymentProcessor />
              </AppLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />

      {/* Lawyer routes */}
      <Route path="/lawyer-dashboard" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerDashboard />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-profile" element={<LawyerRegistration />} />
      
      <Route path="/pending-approval" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <PendingApprovalScreen />
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-profile-view" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerProfile />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-settings" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerSettings />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-cases" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerCases />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-wallet" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerWallet />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-ai-assistant" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerAIAssistant />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-chats" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerChats />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-chat-conversation/:caseId" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerChatConversation />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-reschedule-appointment" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerRescheduleAppointment />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-profile-settings" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerProfileSettings />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-working-hours" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerWorkingHours />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-set-availability" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerSetAvailability />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/lawyer-ratings-reviews" element={
        <ProtectedRoute>
          <RoleBasedRoute allowedRoles={['lawyer']}>
            <ProfileRequiredRoute fallbackPath="/lawyer-profile">
              <LawyerLayout>
                <LawyerRatingsReviews />
              </LawyerLayout>
            </ProfileRequiredRoute>
          </RoleBasedRoute>
        </ProtectedRoute>
      } />

      {/* Fallback route - redirect based on authentication and role */}
      <Route path="*" element={
        isAuthenticated ? (
          userRole === 'lawyer' ? (
            isProfileComplete ? (
              <Navigate to="/lawyer-dashboard" replace />
            ) : (
              <Navigate to="/lawyer-profile" replace />
            )
          ) : userRole === 'admin' ? (
            <Navigate to="/admin-dashboard" replace />
          ) : (
            isProfileComplete ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/profile" replace />
            )
          )
        ) : (
          <Navigate to="/intro/statistics" replace />
        )
      } />
    </Routes>
  );
}

function App() {
  console.log('App component rendering...'); // Debug log
  
  // Auto-cancel service temporarily disabled to prevent database errors
  useEffect(() => {
    console.log('App component mounted, auto-cancel service temporarily disabled');
    // autoCancelService.start();
    
    // Cleanup on unmount
    return () => {
      console.log('App component unmounting, auto-cancel service cleanup');
      // autoCancelService.stop();
    };
  }, []);

  return (
    <ErrorBoundary>
      <FirebaseAuthProvider>
        <AppProvider>
          <div className="min-h-screen w-full bg-bg-primary transition-colors duration-200">
            <AppRoutes />
            <PWARegistration />
          </div>
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </AppProvider>
      </FirebaseAuthProvider>
    </ErrorBoundary>
  );
}

export default App;